import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "urql"

import "../graphql/salesOrderQueries"

import {
  type CustomerPurchaseOrderPricingTotalsInput,
  type CustomerPurchaseOrderPricingTotalsProductResult,
  type CustomerPurchaseOrderPricingTotalsRootTotal,
  GetCustomerPurchaseOrderPricingTotalsDocument,
  type Query,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import type { CpoLineItemFormValues } from "@/screens/CustomerOrders/schemas"
import {
  getCpoPricingTotalsTriggerKey,
  toCustomerPurchaseOrderPricingTotalsInput,
} from "@/screens/CustomerOrders/utils"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const PRICING_TOTALS_DEBOUNCE_MS = 300

export interface CustomerPurchaseOrderPricingTotals {
  total: CustomerPurchaseOrderPricingTotalsRootTotal
  products: CustomerPurchaseOrderPricingTotalsProductResult[]
}

interface UseCalculateCustomerPurchaseOrderPricingTotalsReturn {
  totals: CustomerPurchaseOrderPricingTotals | null
  isFetching: boolean
}

export const useCalculateCustomerPurchaseOrderPricingTotals = (
  customerId: string,
  lineItems: CpoLineItemFormValues[]
): UseCalculateCustomerPurchaseOrderPricingTotalsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [input, setInput] = useState<CustomerPurchaseOrderPricingTotalsInput | null>(null)
  const previousTriggerKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!customerId || !lineItems.length) return

    const triggerKey = getCpoPricingTotalsTriggerKey(lineItems)
    if (triggerKey === previousTriggerKeyRef.current) return

    const timeoutId = setTimeout(() => {
      previousTriggerKeyRef.current = triggerKey
      setInput(toCustomerPurchaseOrderPricingTotalsInput(lineItems, customerId))
    }, PRICING_TOTALS_DEBOUNCE_MS)

    return () => clearTimeout(timeoutId)
  }, [customerId, lineItems])

  const [{ data, fetching, error }] = useQuery<Pick<Query, "salesOrder">>({
    query: GetCustomerPurchaseOrderPricingTotalsDocument,
    variables: { input: input! },
    pause: !input,
  })

  const result = data?.salesOrder.calculateCustomerPurchaseOrderPricingTotals

  const totals = useMemo<CustomerPurchaseOrderPricingTotals | null>(() => {
    if (result?.__typename === "CustomerPurchaseOrderPricingTotalsSuccess") {
      return { total: result.total, products: result.products }
    }
    return null
  }, [result])

  useEffect(() => {
    const message =
      result?.__typename === "CustomerPurchaseOrderPricingTotalsFailure"
        ? result.error.message
        : (error?.graphQLErrors[0]?.message ?? error?.message)

    if (fetching || !message) return

    notify({ type: NotificationType.Error, message })
  }, [result, error, fetching, notify])

  return { totals, isFetching: fetching }
}
